import React, { type FC, useContext, type PropsWithChildren } from 'react';

import { isDefined } from '@lucid/guards';
import { useSmartlingStrings } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import LayoutContext from '../context/layout-context';
import ResourceCard from '../cards/resource-card/resource-card';

const PostPreview: FC<PropsWithChildren<Lucid.Directus.Generated.Blog_Post>> = ({
  categories,
  id,
  image,
  translations,
}) => {
  const translatedData = translations?.[0];
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_blog_aria_label_read_more', 'mktg_learning_campus_collection_topics'],
    shortCode,
  });

  const categoriesToDisplay = categories?.map(category => category.blog_category_id).filter(isDefined);

  const tags = categoriesToDisplay?.length
    ? categoriesToDisplay
        .map(category => {
          const catTranslatedData = category.translations?.[0];

          return catTranslatedData?.name
            ? ({
                default_link_text: catTranslatedData.name,
                final_url: catTranslatedData.path?.url || '/blog',
              } as Lucid.Directus.Generated.Links)
            : undefined;
        })
        .filter(isDefined)
    : undefined;

  return (
    <li key={id} css={{ display: 'grid' }}>
      <ResourceCard
        shouldTruncateText
        heading={translatedData?.heading}
        link={{
          final_url: translatedData?.path?.url,
          isFullCardLink: true,
          accessibleDescription: withSmartling('mktg_blog_aria_label_read_more', {
            title: translatedData?.heading || '',
          }),
        }}
        media={{ image, isFullBleed: true, aspectRatio: '16:9' }}
        tags={{ items: tags, label: withSmartling('mktg_learning_campus_collection_topics') }}
        text={{ value: translatedData?.summary }}
      />
    </li>
  );
};

export default PostPreview;
